// Transactions — Supabase implementation.
//
// The big change from the Firestore version: creating a sale is a single RPC.
// All the pre-flight validation that used to run in the browser (re-deriving
// subtotal, discount, tax and grand total, checking stock, allocating the
// invoice number) now runs inside create_transaction() in Postgres, in one
// real transaction.
//
// Note what CreateTransactionInput no longer carries: prices and totals. The
// old signature accepted them and the server only verified they were
// self-consistent, so a crafted client could sell a 750,000 lens for 1 rupiah
// and pass every check. The database now prices each line from the event's
// stock allocation, so the client cannot influence what anything costs.

import { supabase } from '@/lib/supabase';
import { TX_SELECT, mapTransaction, toIso } from './mappers';
import type {
  AppTransaction,
  DiscountType,
  FulfillmentType,
  PaymentMethod,
  PaymentStatus,
  PaymentTerms,
} from '@/types/domain';

function must<T>(data: T | null, error: { message: string } | null): T {
  if (error) throw new Error(error.message);
  if (data == null) throw new Error('Data tidak ditemukan');
  return data;
}

// ==== Reads ====

export async function getTransaction(id: string): Promise<AppTransaction | null> {
  const { data, error } = await supabase
    .from('transactions_view')
    .select(TX_SELECT)
    .eq('id', id)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data ? mapTransaction(data as never) : null;
}

export async function listTransactionsByEvent(
  eventId: string,
  max = 200,
): Promise<AppTransaction[]> {
  const { data, error } = await supabase
    .from('transactions_view')
    .select(TX_SELECT)
    .eq('event_id', eventId)
    .order('created_at', { ascending: false })
    .limit(max);
  return must(data, error).map((r) => mapTransaction(r as never));
}

export async function listRecentTransactions(max = 50): Promise<AppTransaction[]> {
  const { data, error } = await supabase
    .from('transactions_view')
    .select(TX_SELECT)
    .order('created_at', { ascending: false })
    .limit(max);
  return must(data, error).map((r) => mapTransaction(r as never));
}

export async function listTransactionsByCustomer(
  customerId: string,
  max = 100,
): Promise<AppTransaction[]> {
  const { data, error } = await supabase
    .from('transactions_view')
    .select(TX_SELECT)
    .eq('customer_id', customerId)
    .order('created_at', { ascending: false })
    .limit(max);
  return must(data, error).map((r) => mapTransaction(r as never));
}

// ==== Create ====

export interface CreateTransactionItem {
  productId: string;
  /**
   * Which batch to sell from. Required whenever the product has more than one
   * lot in the event — create_transaction() refuses to guess, because guessing
   * puts the wrong expiry date on a customer's invoice.
   */
  allocationId?: string;
  qty: number;
}

export interface CreateTransactionInput {
  eventId: string;
  /** Only what the customer is buying. Prices come from the allocation. */
  items: CreateTransactionItem[];
  paymentMethod: PaymentMethod;
  fulfillment: FulfillmentType;
  discountType?: DiscountType;
  discountValue?: number;
  taxEnabled?: boolean;
  taxRate?: number;
  customerId?: string | null;
  notes?: string | null;
  paymentTerms?: PaymentTerms;
  /** epoch ms; required when paymentTerms is 'tempo'. */
  dueDate?: number | null;
  /** Down payment for tempo sales. */
  downPayment?: number;
  /** Cash overpayment handed back at the counter. */
  changeAmount?: number;
  /** Promo ids to apply, in order. The server computes what they are worth. */
  promoIds?: string[];
}

export interface CreateTransactionResult {
  id: string;
  invoiceNumber: string;
  paymentStatus: PaymentStatus;
}

export async function createTransaction(
  input: CreateTransactionInput,
): Promise<CreateTransactionResult> {
  if (input.items.length === 0) throw new Error('Transaksi kosong: minimal 1 item');

  const { data, error } = await supabase.rpc('create_transaction', {
    p_event_id: input.eventId,
    p_items: input.items.map((i) => ({
      product_id: i.productId,
      allocation_id: i.allocationId ?? null,
      qty: i.qty,
    })),
    p_payment_method: input.paymentMethod,
    p_fulfillment: input.fulfillment,
    p_discount_type: input.discountType ?? undefined,
    p_discount_value: input.discountValue ?? 0,
    p_tax_enabled: input.taxEnabled ?? false,
    p_tax_rate: input.taxRate ?? 0,
    p_customer_id: input.customerId ?? undefined,
    p_notes: input.notes ?? undefined,
    p_payment_terms: input.paymentTerms ?? 'lunas',
    p_due_date: toIso(input.dueDate) ?? undefined,
    p_down_payment: input.downPayment ?? 0,
    p_change_amount: input.changeAmount ?? 0,
    p_promos: (input.promoIds ?? []).map((id) => ({ promo_id: id })),
  });

  // Postgres RAISE messages are already user-facing Indonesian ("Stok X tidak
  // cukup…"), so they surface unchanged rather than being wrapped.
  if (error) throw new Error(error.message);
  const row = data as unknown as {
    id: string;
    invoice_number: string;
    payment_status: PaymentStatus;
  };
  return {
    id: row.id,
    invoiceNumber: row.invoice_number,
    paymentStatus: row.payment_status,
  };
}

// ==== Mutations ====

/** Mark a "kirim nanti" order as handed over. Does not touch stock. */
export async function fulfillTransaction(transactionId: string): Promise<void> {
  const { error } = await supabase.rpc('fulfill_transaction', {
    p_transaction_id: transactionId,
  });
  if (error) throw new Error(error.message);
}

export interface VoidTransactionInput {
  id: string;
  reason: string;
  /** Required for a cashier: the owner's approval PIN typed at the till. */
  ownerPin?: string;
}

/**
 * Void a sale. Status-only plus stock restoration, done by the database —
 * see void_transaction() for what it refuses (returns, recorded payments,
 * closed events/lots) and who may (owner directly; cashier with the PIN).
 */
export async function voidTransaction(input: VoidTransactionInput): Promise<void> {
  const { error } = await supabase.rpc('void_transaction', {
    p_transaction_id: input.id,
    p_reason: input.reason.trim(),
    p_owner_pin: input.ownerPin?.trim() || null,
  });
  if (error) throw new Error(error.message);
}

export interface UpdateFulfillmentInput {
  transactionId?: string;
  /** Ignored — fulfilment no longer moves stock, so there is nothing to vary. */
  status?: string;
  notes?: string;
}

/** Back-compat wrapper around fulfillTransaction(). */
export async function updateFulfillment(
  input: UpdateFulfillmentInput | string,
): Promise<void> {
  const id = typeof input === 'string' ? input : input.transactionId;
  if (!id) throw new Error('transactionId wajib diisi');
  await fulfillTransaction(id);
}

export interface UpdateTransactionInput {
  notes?: string | null;
}

/**
 * Owner-only edit of the non-monetary fields. RLS enforces the role.
 *
 * Only `notes` is editable here. Payment status is not a free field — it is
 * derived from the ledger (paid, returned, cancelled by void) — and the old
 * dialog's "Cancelled" option was silently dropped by this function for months.
 * Void is its own RPC with stock restoration and an approver.
 */
export async function updateTransaction(
  id: string,
  input: UpdateTransactionInput,
): Promise<void> {
  const patch: { notes?: string | null } = {};
  // Absent ≠ cleared: an edit that did not touch notes must not null them.
  if ('notes' in input) patch.notes = input.notes ?? null;
  if (Object.keys(patch).length === 0) return;
  const { error } = await supabase.from('transactions').update(patch).eq('id', id);
  if (error) throw new Error(error.message);
}

/**
 * What the next invoice number will look like, for display before checkout.
 * Advisory only — the real number is allocated inside create_transaction()
 * under a row lock, so a concurrent sale can claim this one first.
 */
export async function previewNextInvoiceNumber(eventId: string): Promise<string> {
  const { data, error } = await supabase
    .from('events')
    .select('invoice_counter')
    .eq('id', eventId)
    .single();
  if (error) throw new Error(error.message);
  const next = (data?.invoice_counter ?? 0) + 1;
  const d = new Date();
  const stamp = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(
    d.getDate(),
  ).padStart(2, '0')}`;
  return `INV-${stamp}-${String(next).padStart(4, '0')}`;
}
